import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cookies are httpOnly session cookies — always send them.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
}

export function AuthProvider({ children }) {
  const [principal, setPrincipal] = useState(null); // { role, id, name, email, healthie_id }
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // fn to run after login

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setPrincipal(data.authenticated ? data : null);
    } catch {
      setPrincipal(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const memberLogin = useCallback(async (healthie_id, email) => {
    const { data } = await axios.post(`${API}/auth/member/login`, { healthie_id, email });
    setPrincipal(data);
    return data;
  }, []);

  const staffLogin = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/staff/login`, { email, password });
    setPrincipal(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.debug("logout request failed (session cleared locally):", err);
    }
    setPrincipal(null);
  }, []);

  const openLogin = useCallback((action = null) => {
    setPendingAction(() => action);
    setLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    setPendingAction(null);
  }, []);

  const isMember = principal?.role === "member";
  const isStaff = principal?.role === "staff";

  const value = {
    principal,
    isMember,
    isStaff,
    memberKey: isMember ? principal.id : null,
    loading,
    loginOpen,
    openLogin,
    closeLogin,
    pendingAction,
    setPendingAction,
    refresh,
    memberLogin,
    staffLogin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
